import React, { useEffect, useMemo } from 'react';
import PageHeader from 'components/common/general/layouts/PageHeader';
import { BulletTabs, Spinner } from '@insurancemenu/imenu-components-lib';
import { useProposalUrlParams } from 'global/hooks/useProposalUrlParams';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import OutdatedFilesModal from 'components/generate-proposal/OutdatedFilesModal';
import ChooseTheWayPageStore, {
  ChooseTheWayPageStoreContext,
  useChooseTheWayPageStore,
} from 'store/pages/proposal-generation/chooseTheWayPageStore';
import {
  SHOW_THE_WAY_BULLET_TABS,
  PRE_UPLOAD_SPINNER_TEST_ID,
} from 'components/generate-proposal/choose-the-way/constants';
import FirstStep from 'components/generate-proposal/choose-the-way/FirstStep';
import SecondStep from 'components/generate-proposal/choose-the-way/SecondStep';
import ProposalStatusLayout from 'components/common/proposal/ProposalStatusLayout';
import { PageWrapperStyled } from '../../../globalStyles';
import { SpinnerPlaceholder } from './styles';

const PageContent: React.FC = observer(() => {
  const history = useHistory();
  const { url, regenerateFlow, proposalType } = useProposalUrlParams();
  const {
    wantsToUploadCustomFile,
    isLoading,
    currentStepController,
    areFilesIdentical,
    isInitialLoading,
    currentStepIndex,
    redirectBack,
    saveStep,
    completeFlow,
    onNavigateStepBack,
    preparePage,
  } = useChooseTheWayPageStore();

  const showGeneratedProposal = areFilesIdentical && !regenerateFlow;

  useEffect(() => {
    void preparePage();
  }, []);

  useEffect(() => {
    if (showGeneratedProposal) {
      history.push(`/generate-proposal/ready/${url}&proposalType=${proposalType}`);
    }
  }, [areFilesIdentical, regenerateFlow]);

  return (
    <>
      <ProposalStatusLayout>
        <PageWrapperStyled>
          {isInitialLoading || showGeneratedProposal ? (
            <SpinnerPlaceholder>
              <Spinner size="10rem" data-test-id={PRE_UPLOAD_SPINNER_TEST_ID} />
            </SpinnerPlaceholder>
          ) : (
            <>
              <PageHeader title="How to generate proposal?" onRedirect={redirectBack} />
              <BulletTabs
                title="Step"
                tabs={[<FirstStep key={0} />, <SecondStep key={1} />]}
                onBack={onNavigateStepBack}
                onNext={saveStep}
                onComplete={completeFlow}
                disableNextBtn={!currentStepController.isValid}
                disableCompleteBtn={!currentStepController.isValid}
                disableBackBtn={currentStepIndex === 0}
                completeBtnText={wantsToUploadCustomFile ? 'Upload & Continue' : 'Continue'}
                isLoading={isLoading}
                data-test-id={SHOW_THE_WAY_BULLET_TABS}
              />
            </>
          )}
        </PageWrapperStyled>
      </ProposalStatusLayout>
      <OutdatedFilesModal />
    </>
  );
});

const ChooseTheWayPage: React.FC = () => {
  const { url, cartId, groupId, periodId, catalogId, proposalType } = useProposalUrlParams();

  const store = useMemo(
    () =>
      new ChooseTheWayPageStore({
        urlParams: url,
        cartId,
        groupId,
        enrollmentPeriodId: periodId,
        catalogId,
        proposalType,
      }),
    [url, cartId, groupId, periodId],
  );
  return (
    <ChooseTheWayPageStoreContext.Provider value={store}>
      <PageContent />
    </ChooseTheWayPageStoreContext.Provider>
  );
};

export default ChooseTheWayPage;
